import type {MutableRefObject} from 'react'
import type {
  ExternalStoreAdapter,
  AppendMessage,
  MessageStatus,
  ThreadSuggestion,
  ThreadMessageLike,
} from '@assistant-ui/react'
import {removePendingRun, requestPendingRunsReconcile} from './pendingRuns'
import {
  dispatchChatFilesChanged,
  hasFilesChanged,
  pickFilesChangedFields,
  type FilesChangedFields,
} from './filesChanged'
import {forgetPendingRun} from './services/chatApi'
import {
  type AgentStep,
  type Message,
  type AssistantTimelineV2,
  generateUniqueId,
} from './hooks/useChatMessageFormatting'
import type {AgentAnswerFormat} from './formatAgentXmlToMarkdown'
import {coerceAssistantMessageContent} from './coerceAssistantMessageContent'
import {formatAgentXmlToMarkdown} from './formatAgentXmlToMarkdown'
import type {
  ChunkCompleteMetadata,
  StepPayload,
  SendMessageOptions,
  AttachedFileRef,
} from './BedrockSocketProvider'
import type {FileTag} from '@/shared/utils/crud-helper/models'
import {mergeAssistantTimelines} from './mergeAssistantTimeline'
import {normalizeAssistantTimeline} from './timelineUtils'
import {applyToolCallStartToLiveLanes, flushPreToolPreambleLanes} from './liveTurnLayout'
import {getLogger} from '@/shared/utils'
import {assistantRowIdForRun, runIdOfPersistedRow} from './inFlightTurn'
import {formatToolDisplayLabel} from './toolDisplayLabels'
import {skillNameFromReadFileArgs} from './skillPaths'
import {CONTEXT_SUMMARIZED_ALERT_MESSAGE, type ContextUsage} from './omicsContextUsage'
import {omicsMessageToAssistantParts, sanitizeAssistantParts} from './omicsMessageToAssistantParts'
import {
  captureAssistantUiIndexFingerprint,
  didAssistantUiIndexSpaceShrink,
  EMPTY_ASSISTANT_UI_INDEX_FINGERPRINT,
  padAssistantLikeContent,
  raiseAssistantUiPartHighWater,
  type AssistantUiIndexFingerprint,
} from './assistantUiIndexSafety'
import {
  applyUserCancelToThread,
  findAssistantIndexByRunId,
  findLastAssistantIndex,
  isAssistantRow,
  patchAssistantAfterUserCancel,
  shouldAcceptStreamEvent,
  shouldApplyNormalCompletion,
  shouldSuppressAdapterSetMessages,
  upsertAssistantMessageById,
  type CancelFinalizeSnapshot,
} from './hooks/cancelThreadHelpers'
import {OMICS_WELCOME_PROMPTS} from './omicsWelcomePrompts'
import type {OmicsChatSendOptionsRef} from './hooks/useBedrockStoreAdapter'

export interface SessionWireContext {
  conversationId: string
  agentSessionId: string
}

export type SessionSendFn = (
  text: string,
  messageId: string,
  session: SessionWireContext,
  options?: SendMessageOptions,
) => void

export type SessionCancelFn = (messageId: string, session: SessionWireContext) => void

export interface BedrockSessionControllerDeps {
  processStreamingContent: (content: string) => string
  createAgentResponseFromSteps: (
    id: string,
    finalContent: string,
    steps: AgentStep[],
    toolsUsed?: string[],
    kbSources?: Array<{source: string; relevance?: number}>,
    internetSources?: Array<{title?: string; url: string}>,
    timeline?: AssistantTimelineV2,
    answerFormat?: AgentAnswerFormat,
  ) => Message
  createBotMessage: (content: string, id?: string, timeline?: AssistantTimelineV2) => Message
  rawStreamModeRef?: MutableRefObject<boolean>
  sendOptionsRef: MutableRefObject<OmicsChatSendOptionsRef>
  sendFnRef: MutableRefObject<SessionSendFn | null>
  cancelFnRef: MutableRefObject<SessionCancelFn | null>
  /** This conversation's composer selection (attachments + model), read when a turn starts. */
  composerSelectionRef: MutableRefObject<ComposerSelectionFn>
  clearFileTagsRef: MutableRefObject<ClearFileTagsFn | null>
}

/** Empties one conversation's composer chips once they have been sent with a turn. */
export type ClearFileTagsFn = (conversationKey: string) => void

/** What one conversation's composer has selected for its next turn. */
export interface ComposerSelection {
  fileTags: readonly FileTag[]
  /** Chat model picked in this tab; null lets the server use its default. */
  chatModelId: string | null
}

export type ComposerSelectionFn = (conversationId: string) => ComposerSelection

function emptyTimeline(): AssistantTimelineV2 {
  return {schema_version: 2, entries: []}
}

const SOCKET_CLOSED_ERROR = 'WebSocket connection closed'

const TRANSPORT_ERRORS_SUPPRESS_WHEN_IDLE = new Set([
  'WebSocket connection error',
  'WebSocket not connected',
  // A close is routine when nothing is in flight (idle tab, laptop asleep). It must still reach
  // onError so a turn that *was* running stops looking like it still is, but it should not paint an
  // error into an idle conversation.
  SOCKET_CLOSED_ERROR,
])

const OMICS_THREAD_SUGGESTIONS: readonly ThreadSuggestion[] = OMICS_WELCOME_PROMPTS.map((prompt) => ({
  prompt,
}))

function appendStepToEntries(
  entries: AssistantTimelineV2['entries'],
  step: StepPayload,
): AssistantTimelineV2['entries'] {
  const next = [...entries]
  if (step.type === 'tool_call_start') {
    next.push({
      kind: 'tool_call',
      tool_call_id: step.toolCallId!,
      tool_name: step.toolName!,
      tool_input: step.toolInput ?? {},
      ...(step.displaySummary ? {display_summary: step.displaySummary} : {}),
      step_index: step.stepIndex,
    })
  } else if (step.type === 'tool_call_end') {
    next.push({
      kind: 'tool_result',
      tool_call_id: step.toolCallId!,
      tool_name: step.toolName!,
      content: step.toolResult ?? '',
      ok: step.toolOk !== false,
      error: step.toolError ?? null,
      step_index: step.stepIndex,
    })
  } else if (step.type === 'step' && step.content !== undefined && step.node) {
    const hasTextSoFar = next.some((e) => e.kind === 'text')
    const segment_kind = step.segmentKind ?? (!hasTextSoFar ? 'preamble' : 'reasoning')
    next.push({
      kind: 'text',
      segment_kind,
      content: step.content,
      step_index: step.stepIndex,
    })
  }
  return next
}

function assistantMessageStatus(msg: Message): MessageStatus {
  if (msg.isStreamingTurn) {
    return {type: 'running'}
  }
  return {type: 'complete', reason: 'stop'}
}

/**
 * Files a failed turn changed are real too: refresh the Files panel for them and return a copy of
 * the fields to store on the error message (so the run footer lists them); empty when there are none.
 */
function announceFilesChanged(fields: FilesChangedFields): FilesChangedFields {
  const copy = pickFilesChangedFields(fields)
  if (copy.filesChanged) dispatchChatFilesChanged(copy.filesChanged)
  return copy
}

function resolveRunMessageId(messageId: string, lastMessageId: string | null): string | null {
  if (messageId && messageId !== 'unknown') {
    return messageId
  }
  return lastMessageId
}

function snapshotFromMetadata(metadata?: ChunkCompleteMetadata): CancelFinalizeSnapshot {
  const serverTl = metadata?.timeline ? normalizeAssistantTimeline(metadata.timeline) : undefined
  return {
    ...(serverTl && serverTl.entries.length > 0 ? {timeline: serverTl} : {}),
    ...(metadata?.toolsUsed?.length ? {toolsUsed: metadata.toolsUsed} : {}),
    ...pickFilesChangedFields(metadata),
  }
}

export function convertMessage(msg: Message): ThreadMessageLike {
  if (msg.type === 'user') {
    return {
      role: 'user' as const,
      id: msg.id,
      content: [{type: 'text' as const, text: msg.content}],
    }
  }

  return {
    role: 'assistant' as const,
    id: msg.id,
    content: sanitizeAssistantParts(omicsMessageToAssistantParts(msg)) as ThreadMessageLike['content'],
    status: assistantMessageStatus(msg),
  }
}

/** Prompt sent when the user attaches chips without typing anything. */
function defaultPromptForTags(tags: readonly FileTag[]): string {
  const list = tags.map((t) => `[${t.name}]`).join(', ')
  const one = tags.length === 1
  if (tags.every((t) => t.type === 'job')) return `Please check the following ${one ? 'job' : 'jobs'}: ${list}`
  if (tags.some((t) => t.type === 'job')) return `Please analyze the following items: ${list}`
  return `Please analyze the following ${one ? 'file' : 'files'}: ${list}`
}

export class BedrockSessionController {
  conversationId: string
  agentSessionId: string
  title: string
  messages: Message[] = []
  isRunning = false
  threadResyncKey = 0
  threadContextUsage: ContextUsage | null = null
  hydrated = false
  lastActivityAt: number
  /**
   * Run id of a turn started before a page reload that this tab is waiting on. There is no live
   * stream for it — the reply arrives by refetching the conversation.
   */
  resumedRunMessageId: string | null = null
  /** Resumed runs the user stopped; a refetch still shows them unanswered and must not resume them. */
  readonly abandonedRunIds = new Set<string>()

  private readonly deps: BedrockSessionControllerDeps
  private listener: (() => void) | null = null

  private streamingContent = ''
  private streamingStep: number | null = null
  private streamingMessageId: string | null = null
  private pendingTimeline: AssistantTimelineV2 = emptyTimeline()

  private lastMessageId: string | null = null
  /**
   * Run id of each user row this tab typed. Its optimistic id is local, while the server persists it
   * as `user-<runId>`; this is what lets a refetch recognise it (the reply is keyed by the run id).
   */
  private readonly runIdByLocalUserRowId = new Map<string, string>()
  private toolsUsedThisTurn: string[] = []
  private lastResponseCancelled = false
  private suppressCancelResync = false
  private indexFingerprint: AssistantUiIndexFingerprint = EMPTY_ASSISTANT_UI_INDEX_FINGERPRINT
  /** Peak part count per message id so convertMessage never shrinks tapClientLookup indexes. */
  private partHighWaterByMessageId = new Map<string, number>()

  constructor(conversationId: string, agentSessionId: string, deps: BedrockSessionControllerDeps) {
    this.conversationId = conversationId
    this.agentSessionId = agentSessionId
    this.title = 'Untitled'
    this.lastActivityAt = Date.now()
    this.deps = deps
  }

  setListener(listener: (() => void) | null): void {
    this.listener = listener
  }

  /** Request a React re-render for consumers bound to this session. */
  notifyUpdate(): void {
    this.bump()
  }

  private bump(options?: {remount?: boolean}): void {
    const nextFingerprint = captureAssistantUiIndexFingerprint(this.messages)
    raiseAssistantUiPartHighWater(this.partHighWaterByMessageId, nextFingerprint)
    if (
      options?.remount ||
      didAssistantUiIndexSpaceShrink(this.indexFingerprint, nextFingerprint)
    ) {
      this.threadResyncKey += 1
    }
    this.indexFingerprint = nextFingerprint
    this.listener?.()
  }

  /**
   * assistant-ui applies the adapter in `useEffect` (after paint). Shrinking the part array
   * there throws `tapClientLookup` on still-mounted fibers — pad to the high-water mark instead.
   */
  private convertMessageForRuntime(msg: Message): ThreadMessageLike {
    const converted = convertMessage(msg)
    if (converted.role === 'user' || typeof converted.content === 'string') {
      return converted
    }
    const highWater = Math.max(
      this.partHighWaterByMessageId.get(msg.id) ?? 0,
      converted.content.length,
    )
    this.partHighWaterByMessageId.set(msg.id, highWater)
    const content = padAssistantLikeContent(converted.content, highWater)
    if (content === converted.content) return converted
    return {...converted, content}
  }

  touchActivity(): void {
    this.lastActivityAt = Date.now()
    this.bump()
  }

  get isAwaitingResumedTurn(): boolean {
    return this.resumedRunMessageId != null
  }

  /** Show a turn still running server-side (found on load) as in progress: placeholder, Stop button. */
  resumeInFlightTurn(runMessageId: string): void {
    if (this.isRunning || this.abandonedRunIds.has(runMessageId)) return
    const placeholderId = assistantRowIdForRun(runMessageId)
    this.lastMessageId = runMessageId
    this.streamingMessageId = placeholderId
    this.resumedRunMessageId = runMessageId
    this.messages = upsertAssistantMessageById(this.messages, placeholderId, () =>
      this.deps.createBotMessage('', placeholderId, {schema_version: 2, entries: []}),
    )
    this.isRunning = true
    this.streamingContent = ''
    this.streamingStep = null
    this.pendingTimeline = emptyTimeline()
    this.bump()
  }

  /**
   * The live stream for the running turn ended (bridge time cap, socket closed) while the agent keeps
   * going server-side: keep what streamed so far and wait on the stored reply, as for a resumed turn.
   */
  onDetached(runMessageId?: string): void {
    const runId = resolveRunMessageId(runMessageId ?? '', this.lastMessageId)
    if (!this.isRunning || this.isAwaitingResumedTurn || !runId) return
    this.resumedRunMessageId = runId
    this.bump()
  }

  /**
   * Paths where the turn is not rebuilt from the completion (raw-stream mode, a stale completion)
   * still keep its `files_changed` on the run's assistant row when there is one, so the run footer
   * lists them.
   */
  private storeFilesChangedOnRunRow(messageId: string | null, fields?: FilesChangedFields): void {
    if (!hasFilesChanged(fields)) return
    const runId =
      this.streamingMessageId ?? resolveRunMessageId(messageId ?? '', this.lastMessageId)
    if (!runId) return
    const idx = findAssistantIndexByRunId(this.messages, runId)
    if (idx < 0) return
    const updated = [...this.messages]
    updated[idx] = {...updated[idx], ...pickFilesChangedFields(fields)}
    this.messages = updated
  }

  registerInFlight(messageId: string): void {
    this.lastMessageId = messageId
  }

  onChunk(
    chunk: string,
    messageId: string,
    isComplete: boolean,
    metadata?: ChunkCompleteMetadata,
  ): void {
    const {rawStreamModeRef, processStreamingContent, createAgentResponseFromSteps, createBotMessage} =
      this.deps

    // Whatever the turn's outcome (and in raw-stream mode too), files it wrote are real: refresh the
    // Files panel for them.
    if (isComplete && metadata?.filesChanged?.length) dispatchChatFilesChanged(metadata.filesChanged)

    if (rawStreamModeRef?.current) {
      if (isComplete) {
        this.storeFilesChangedOnRunRow(messageId, metadata)
        this.streamingContent = ''
        this.streamingStep = null
        this.streamingMessageId = null
        this.pendingTimeline = emptyTimeline()
        this.isRunning = false
        this.bump()
      }
      return
    }
    if (!shouldAcceptStreamEvent(this.lastResponseCancelled, isComplete)) {
      return
    }

    if (isComplete) {
      if (this.lastResponseCancelled || metadata?.interrupted) {
        const runId = resolveRunMessageId(messageId, this.lastMessageId)
        const prev = this.messages
        const idx = runId
          ? findAssistantIndexByRunId(prev, runId)
          : findLastAssistantIndex(prev)
        if (idx >= 0) {
          const updated = [...prev]
          updated[idx] = patchAssistantAfterUserCancel(updated[idx], metadata)
          this.messages = updated
        } else {
          this.messages = applyUserCancelToThread(prev, runId, snapshotFromMetadata(metadata))
        }
        this.streamingContent = ''
        this.streamingStep = null
        this.streamingMessageId = null
        this.pendingTimeline = emptyTimeline()
        this.isRunning = false
        this.bump()
        return
      }

      if (!shouldApplyNormalCompletion(this.lastResponseCancelled, metadata?.interrupted)) {
        this.storeFilesChangedOnRunRow(messageId, metadata)
        this.streamingContent = ''
        this.streamingStep = null
        this.streamingMessageId = null
        this.pendingTimeline = emptyTimeline()
        this.isRunning = false
        this.bump()
        return
      }

      const fullContent = this.streamingContent + chunk
      const localTl = this.pendingTimeline
      const serverTl = normalizeAssistantTimeline(metadata?.timeline)
      const merged = mergeAssistantTimelines(
        serverTl,
        localTl.entries.length > 0 ? localTl : undefined,
      )
      const canonicalAnswer = coerceAssistantMessageContent(
        (metadata?.answer && metadata.answer.trim()) ||
          formatAgentXmlToMarkdown(processStreamingContent(fullContent)) ||
          '',
      )

      const timelineForMessage = merged && merged.entries.length > 0 ? merged : undefined

      const updated = [...this.messages]
      const streamId = this.streamingMessageId
      let idx = streamId ? updated.findIndex((m) => m.id === streamId) : -1
      if (idx === -1 && messageId && messageId !== 'unknown') {
        idx = updated.findIndex((m) => m.id === messageId)
      }
      if (idx === -1) {
        for (let i = updated.length - 1; i >= 0; i -= 1) {
          const row = updated[i]
          if (row.type === 'bot' || row.type === 'agent-response') {
            idx = i
            break
          }
        }
      }
      if (idx !== -1) {
        const row = updated[idx]
        const effectiveToolsUsed = metadata?.toolsUsed ?? row.toolsUsed
        const effectiveKbSources = metadata?.kbSources ?? row.kbSources
        const effectiveInternetSources = metadata?.internetSources ?? row.internetSources
        let next: Message = createAgentResponseFromSteps(
          row.id,
          canonicalAnswer,
          [],
          effectiveToolsUsed,
          effectiveKbSources,
          effectiveInternetSources,
          timelineForMessage,
          metadata?.answerFormat,
        )
        if (metadata?.tokenUsage) next = {...next, tokenUsage: metadata.tokenUsage}
        if (metadata?.suggestedFollowups?.length) next = {...next, suggestedFollowups: metadata.suggestedFollowups}
        next = {...next, ...pickFilesChangedFields(metadata)}
        // Deliberately NOT registering metadata.pendingRuns: that list is a model's reading of its
        // own answer, and a mis-read id becomes a tag nothing can resolve. Ask the registry instead
        // — the middleware wrote the real rows during this turn, so one fetch has the truth.
        if (metadata?.pendingRuns?.length) requestPendingRunsReconcile()
        // The turn itself reported these as finished (the user asked, or the agent polled), so drop
        // their tags here rather than waiting for a job.status notification. `removePendingRun`
        // no-ops on ids we aren't tracking, so a stray id in the list is harmless.
        //
        // Server-known runs are dropped there too: the strip is server-sourced, so a local-only
        // removal is undone by the next poll. Deleting (rather than resolving) is deliberate — this
        // turn has already told the user, so a follow-up turn would be redundant and billed.
        metadata?.resolvedRuns?.forEach((runId) => {
          const entry = removePendingRun(runId)
          if (entry?.origin === 'server') {
            void forgetPendingRun(runId).catch((error) => {
              getLogger().warn(`Forgetting resolved run ${runId} failed: ${String(error)}`)
            })
          }
        })
        if (metadata?.modelUsed) next = {...next, modelUsed: metadata.modelUsed}
        if (metadata?.cloudUnits) next = {...next, cloudUnits: metadata.cloudUnits}
        if (metadata?.agentRuntimeLabel) {
          next = {...next, agentRuntimeLabel: metadata.agentRuntimeLabel}
        }
        if (metadata?.contextUsage) {
          next = {...next, contextUsage: metadata.contextUsage}
          this.threadContextUsage = metadata.contextUsage
        }
        if (metadata?.contextCompacted) {
          next = {
            ...next,
            contextCompacted: true,
            streamAlert:
              row.streamAlert?.kind === 'cancel' || row.streamAlert?.kind === 'error'
                ? row.streamAlert
                : {kind: 'info', message: CONTEXT_SUMMARIZED_ALERT_MESSAGE},
          }
        } else if (row.type === 'bot' && row.streamAlert) {
          next = {...next, streamAlert: row.streamAlert}
        }
        updated[idx] = next
      }
      this.messages = updated

      this.streamingContent = ''
      this.streamingStep = null
      this.streamingMessageId = null
      this.pendingTimeline = emptyTimeline()
      this.isRunning = false
      this.touchActivity()
      return
    }

    if (metadata?.stepIndex !== undefined && metadata.stepIndex !== this.streamingStep) {
      const toFlush = processStreamingContent(this.streamingContent)
      this.streamingContent = ''
      this.streamingStep = metadata.stepIndex
      if (toFlush.trim() && this.streamingMessageId) {
        const currentId = this.streamingMessageId
        const tl = this.pendingTimeline
        const prev = this.messages
        const msgIdx = prev.findIndex((m) => m.id === currentId)
        if (msgIdx !== -1) {
          const updated = [...prev]
          const row = updated[msgIdx]
          updated[msgIdx] = {
            ...row,
            ...flushPreToolPreambleLanes(row, toFlush, tl.entries),
            timeline: tl.entries.length > 0 ? tl : row.timeline,
            content: '',
            isStreamingTurn: true,
          }
          this.messages = updated
          this.bump()
        }
      }
    }
    this.streamingContent += chunk

    if (!this.streamingMessageId) {
      const id = messageId && messageId !== 'unknown' ? messageId : generateUniqueId()
      this.streamingMessageId = id
      const tl = this.pendingTimeline
      const emptyTl = tl.entries.length > 0 ? tl : {schema_version: 2 as const, entries: []}
      this.messages = upsertAssistantMessageById(this.messages, id, () =>
        createBotMessage('', id, emptyTl),
      )
      this.bump()
    } else {
      const currentId = this.streamingMessageId
      const processed = processStreamingContent(this.streamingContent)
      const tl = this.pendingTimeline
      const prev = this.messages
      const updated = [...prev]
      const msgIdx = updated.findIndex((m) => m.id === currentId)
      if (msgIdx !== -1) {
        updated[msgIdx] = {
          ...updated[msgIdx],
          content: processed,
          timeline: tl.entries.length > 0 ? tl : updated[msgIdx].timeline,
          isStreamingTurn: true,
        }
        this.messages = updated
        this.bump()
      }
    }
  }

  onStep(step: StepPayload): void {
    const {rawStreamModeRef, processStreamingContent, createBotMessage} = this.deps

    if (rawStreamModeRef?.current) {
      return
    }
    if (!shouldAcceptStreamEvent(this.lastResponseCancelled, false)) {
      return
    }

    let streamAlert: Message['streamAlert'] | undefined
    if (step.type === 'tool_call_end' && step.toolOk === false) {
      const detail =
        (step.toolError && step.toolError.trim()) ||
        (step.toolResult && step.toolResult.trim()) ||
        'unknown error'
      const label = formatToolDisplayLabel(step.toolName ?? '?')
      streamAlert = {
        kind: 'error',
        message: `Tool '${label}' failed: ${detail}`,
      }
    }

    if (step.type === 'tool_call_start' && step.toolName) {
      const tn = step.toolName.trim()
      const isSkillLoad =
        tn === 'use_skill' ||
        (tn === 'read_file' && step.toolInput && skillNameFromReadFileArgs(step.toolInput) != null)
      if (tn && !isSkillLoad && !this.toolsUsedThisTurn.includes(tn)) {
        this.toolsUsedThisTurn = [...this.toolsUsedThisTurn, tn]
      }
    }

    if (step.type === 'tool_call_start') {
      this.pendingTimeline = {
        schema_version: 2,
        entries: appendStepToEntries(this.pendingTimeline.entries, step),
      }
      const tl = this.pendingTimeline
      const flushed = processStreamingContent(this.streamingContent)
      this.streamingContent = ''
      this.streamingStep = null

      const patchBot = (row: Message): Message => ({
        ...row,
        ...applyToolCallStartToLiveLanes(row, step.toolCallId!, flushed),
        timeline: tl,
        content: '',
        isStreamingTurn: true,
      })

      if (!this.streamingMessageId) {
        const id = this.lastMessageId ?? generateUniqueId()
        this.streamingMessageId = id
        this.messages = upsertAssistantMessageById(this.messages, id, (existing) =>
          patchBot(existing ?? createBotMessage('', id, tl)),
        )
        this.bump()
        return
      }

      const streamId = this.streamingMessageId
      const prev = this.messages
      const updated = [...prev]
      const idx = updated.findIndex((m) => m.id === streamId)
      if (idx === -1) return
      updated[idx] = patchBot(updated[idx])
      this.messages = updated
      this.bump()
      return
    }

    this.pendingTimeline = {
      schema_version: 2,
      entries: appendStepToEntries(this.pendingTimeline.entries, step),
    }
    const tl = this.pendingTimeline

    let stepIndexChanged = false
    let processedNewStep = ''
    if (step.type === 'step' && step.content !== undefined && step.node) {
      const stepBodyRaw = coerceAssistantMessageContent(step.content)
      processedNewStep = processStreamingContent(stepBodyRaw)
      const prevStepIndex = this.streamingStep
      stepIndexChanged = step.stepIndex !== undefined && step.stepIndex !== prevStepIndex
      this.streamingContent = stepBodyRaw
      this.streamingStep = step.stepIndex ?? null
    }

    const applyTextStepPatch = (row: Message): Message => {
      if (step.type !== 'step' || step.content === undefined || !step.node) {
        return {...row, timeline: tl, ...(streamAlert !== undefined ? {streamAlert} : {})}
      }
      const prevStream = stepIndexChanged ? processStreamingContent(row.content || '') : ''
      let next = row
      if (stepIndexChanged && prevStream.trim()) {
        next = {...next, ...flushPreToolPreambleLanes(next, prevStream, tl.entries)}
      }
      return {
        ...next,
        content: processedNewStep,
        timeline: tl,
        isStreamingTurn: true,
        ...(streamAlert !== undefined ? {streamAlert} : {}),
      }
    }

    if (!this.streamingMessageId) {
      const id = this.lastMessageId ?? generateUniqueId()
      this.streamingMessageId = id
      this.messages = upsertAssistantMessageById(this.messages, id, (existing) =>
        applyTextStepPatch(existing ?? createBotMessage('', id, tl)),
      )
      this.bump()
      return
    }

    const streamId = this.streamingMessageId
    const prev = this.messages
    const updated = [...prev]
    const idx = updated.findIndex((m) => m.id === streamId)
    if (idx === -1) return
    updated[idx] = applyTextStepPatch(updated[idx])
    this.messages = updated
    this.bump()
  }

  onError(error: string, options: FilesChangedFields = {}): void {
    const {rawStreamModeRef} = this.deps
    const fileChanges = announceFilesChanged(options)

    if (rawStreamModeRef?.current) {
      this.storeFilesChangedOnRunRow(this.lastMessageId, fileChanges)
      this.isRunning = false
      this.streamingContent = ''
      this.streamingStep = null
      this.streamingMessageId = null
      this.pendingTimeline = emptyTimeline()
      this.bump()
      return
    }

    // A resumed turn never streams over this socket, so losing it changes nothing for that turn.
    if (TRANSPORT_ERRORS_SUPPRESS_WHEN_IDLE.has(error) && this.isAwaitingResumedTurn) return
    // The agent outlives the socket: a close mid-turn hands the turn to the stored-reply poll.
    if (error === SOCKET_CLOSED_ERROR && this.isRunning && this.lastMessageId) {
      this.onDetached()
      return
    }

    const suppressIdleTransport =
      TRANSPORT_ERRORS_SUPPRESS_WHEN_IDLE.has(error) && !this.isRunning

    this.isRunning = false
    this.streamingContent = ''
    this.streamingStep = null
    this.streamingMessageId = null
    this.pendingTimeline = emptyTimeline()

    if (suppressIdleTransport) {
      getLogger().debug('Suppressed idle WebSocket transport error from chat transcript', {error})
      return
    }

    this.messages = [
      ...this.messages,
      {
        id: generateUniqueId(),
        type: 'bot',
        content: `⚠️ ${error}`,
        ...fileChanges,
      },
    ]
    this.bump()
  }

  onRecursionLimit(payload: {error: string; canResume: boolean} & FilesChangedFields): void {
    const {rawStreamModeRef} = this.deps
    const fileChanges = announceFilesChanged(payload)

    if (rawStreamModeRef?.current) {
      this.storeFilesChangedOnRunRow(this.lastMessageId, fileChanges)
      this.isRunning = false
      this.streamingContent = ''
      this.streamingStep = null
      this.streamingMessageId = null
      this.pendingTimeline = emptyTimeline()
      this.bump()
      return
    }
    this.messages = [
      ...this.messages,
      {
        id: generateUniqueId(),
        type: 'bot',
        content: `⚠️ ${payload.error}`,
        recursionLimitHit: true,
        ...fileChanges,
      },
    ]
    this.isRunning = false
    this.streamingContent = ''
    this.streamingStep = null
    this.streamingMessageId = null
    this.pendingTimeline = emptyTimeline()
    this.bump()
  }

  async onNew(message: AppendMessage): Promise<void> {
    let text = ''
    for (const part of message.content) {
      if (part.type === 'text') {
        text = part.text
        break
      }
    }
    this.startTurn(text, {useComposerFiles: true})
  }

  /**
   * Start the follow-up turn for finished background runs. Returns false when a turn is already
   * running, so the caller can keep the runs and try again once it ends. It takes the composer's
   * model but not its file chips: those belong to whatever the user is about to send.
   */
  startContinuation(prompt: string): boolean {
    return this.startTurn(prompt, {useComposerFiles: false})
  }

  /**
   * Every path that starts a turn lands here (composer, follow-up chips, run continuations), so
   * these are the one frontend checks. One turn at a time: the streaming state below is a single
   * slot per conversation, and a second turn started mid-stream would take it over, writing the
   * rest of the first reply into the second one's bubble.
   */
  private startTurn(rawText: string, {useComposerFiles}: {useComposerFiles: boolean}): boolean {
    const {sendOptionsRef, sendFnRef, composerSelectionRef} = this.deps
    // The agent enforces the same balance rule server-side.
    if (sendOptionsRef.current.canStartTurn === false) return false
    if (this.isRunning) return false

    let text = rawText
    const selection = composerSelectionRef.current(this.conversationId)
    const tags = useComposerFiles ? selection.fileTags : []
    const {chatModelId} = selection
    if (!text.trim() && tags.length === 0) return false

    if (!text.trim() && tags.length > 0) {
      text = defaultPromptForTags(tags)
    }

    const userMsg: Message = {
      id: generateUniqueId(),
      type: 'user',
      content: text,
      ...(tags.length ? {attachedFiles: [...tags]} : {}),
    }
    // The chips now belong to this prompt's bubble; the composer starts clean for the next one.
    if (tags.length) this.deps.clearFileTagsRef.current?.(this.conversationId)
    const msgId = generateUniqueId()
    this.runIdByLocalUserRowId.set(userMsg.id, msgId)
    this.lastMessageId = msgId
    this.streamingMessageId = msgId
    this.messages = upsertAssistantMessageById([...this.messages, userMsg], msgId, () =>
      this.deps.createBotMessage('', msgId, {schema_version: 2, entries: []}),
    )
    this.isRunning = true
    this.streamingContent = ''
    this.streamingStep = null
    this.pendingTimeline = emptyTimeline()
    this.toolsUsedThisTurn = []

    const attachedFileRefs: AttachedFileRef[] = tags.map((tag: FileTag) =>
      tag.type === 'job'
        ? {name: tag.name, type: 'job', run_id: tag.runId, ...(tag.tool ? {tool: tag.tool} : {})}
        : {name: tag.name, path: tag.cloudPath, type: tag.type},
    )

    const wasCancelled = this.lastResponseCancelled
    this.lastResponseCancelled = false

    const {mode} = sendOptionsRef.current

    const options: SendMessageOptions = {
      mode,
      ...(chatModelId ? {chatModelId} : {}),
      ...(attachedFileRefs.length ? {attachedFileRefs} : {}),
      ...(wasCancelled ? {previousCancelled: true} : {}),
    }
    this.registerInFlight(msgId)
    sendFnRef.current?.(text, msgId, {
      conversationId: this.conversationId,
      agentSessionId: this.agentSessionId,
    }, options)
    this.touchActivity()
    return true
  }

  async onCancel(): Promise<void> {
    const {rawStreamModeRef, cancelFnRef} = this.deps

    const msgId = this.lastMessageId
    if (!msgId) return

    this.suppressCancelResync = true
    cancelFnRef.current?.(msgId, {
      conversationId: this.conversationId,
      agentSessionId: this.agentSessionId,
    })

    this.isRunning = false
    this.lastResponseCancelled = true
    if (this.resumedRunMessageId) {
      this.abandonedRunIds.add(this.resumedRunMessageId)
      this.resumedRunMessageId = null
    }
    const runIdAtCancel = this.streamingMessageId ?? this.lastMessageId
    const cancelSnapshot = {
      timeline:
        this.pendingTimeline.entries.length > 0 ? {...this.pendingTimeline} : undefined,
      toolsUsed:
        this.toolsUsedThisTurn.length > 0 ? [...this.toolsUsedThisTurn] : undefined,
    }
    this.streamingContent = ''
    this.streamingStep = null
    this.streamingMessageId = null
    this.pendingTimeline = emptyTimeline()
    this.toolsUsedThisTurn = []

    if (rawStreamModeRef?.current) {
      this.bump()
      return
    }
    this.messages = applyUserCancelToThread(this.messages, runIdAtCancel, cancelSnapshot)
    this.bump({remount: true})
  }

  onStreamCancelledAck(ackMessageId?: string): void {
    if (!this.lastResponseCancelled) {
      return
    }
    const runId = ackMessageId?.trim() || this.lastMessageId
    const cancelSnapshot: CancelFinalizeSnapshot = {
      timeline:
        this.pendingTimeline.entries.length > 0 ? {...this.pendingTimeline} : undefined,
      toolsUsed:
        this.toolsUsedThisTurn.length > 0 ? [...this.toolsUsedThisTurn] : undefined,
    }
    this.messages = applyUserCancelToThread(this.messages, runId, cancelSnapshot)
    this.bump()
  }

  /**
   * Adopt the server's messages without destroying a turn in flight.
   *
   * `replaceThreadMessages` forces `isRunning = false` and drops the streaming buffers, which is
   * right when opening a conversation and catastrophic while one is streaming — the live turn would
   * vanish mid-sentence and the composer would un-stick. A refetch triggered by another device
   * (`chat.updated`) can land at any moment, so it comes through here instead: server rows win,
   * and anything this browser holds that the server has not persisted yet (the optimistic user row,
   * the streaming reply) is kept on the end, where it chronologically belongs.
   *
   * Rows are matched by turn, not id: a turn typed here carries local ids (the optimistic user row,
   * the reply under the bare run id) while the server persists it as `user-<runId>` /
   * `assistant-<runId>`. The live turn keeps its local copy even once persisted — the agent saves
   * the reply before the stream ends (follow-up suggestions, RUN_FINISHED still patch it here).
   */
  mergeThreadMessages(msgs: readonly Message[]): void {
    if (!this.isRunning) {
      this.replaceThreadMessages(msgs)
      return
    }
    const serverIds = new Set(msgs.map((m) => m.id))
    const liveRunId = this.lastMessageId
    const liveTurnKeys = new Set(
      this.messages
        .filter((m) => !serverIds.has(m.id) && this.runIdOfRow(m) === liveRunId)
        .map((m) => this.turnKeyOfRow(m)),
    )
    const serverRows = this.withKnownAttachments(msgs).filter((m) => !liveTurnKeys.has(this.turnKeyOfRow(m)))
    const serverTurnKeys = new Set(serverRows.map((m) => this.turnKeyOfRow(m)))
    const localOnly = this.messages.filter(
      (m) => !serverIds.has(m.id) && !serverTurnKeys.has(this.turnKeyOfRow(m)),
    )
    this.messages = [...serverRows, ...localOnly]
    this.hydrated = true
    this.bump()
  }

  /**
   * Whether this run is streaming live in this tab. Its own `chat.updated` echo then brings nothing
   * a merge would keep — the live copy wins until the stream ends.
   */
  isStreamingRun(runId: string): boolean {
    return this.isRunning && !this.isAwaitingResumedTurn && this.lastMessageId === runId
  }

  private runIdOfRow(msg: Message): string {
    if (msg.type === 'user') {
      return this.runIdByLocalUserRowId.get(msg.id) ?? runIdOfPersistedRow(msg.id) ?? msg.id
    }
    return runIdOfPersistedRow(msg.id) ?? msg.id
  }

  private turnKeyOfRow(msg: Message): string {
    const role = msg.type === 'user' ? 'user' : isAssistantRow(msg) ? 'assistant' : msg.type
    return `${role}:${this.runIdOfRow(msg)}`
  }

  /**
   * The server does not persist a prompt's chips, so a refetch would strip them from the bubbles.
   * Keep the ones this tab sent on the matching server user row.
   */
  private withKnownAttachments(msgs: readonly Message[]): Message[] {
    const byTurn = new Map<string, readonly FileTag[]>()
    for (const m of this.messages) {
      if (m.type === 'user' && m.attachedFiles?.length) byTurn.set(this.turnKeyOfRow(m), m.attachedFiles)
    }
    if (byTurn.size === 0) return [...msgs]
    return msgs.map((m) => {
      if (m.type !== 'user' || m.attachedFiles?.length) return m
      const attachedFiles = byTurn.get(this.turnKeyOfRow(m))
      return attachedFiles ? {...m, attachedFiles} : m
    })
  }

  replaceThreadMessages(msgs: readonly Message[]): void {
    const next = this.withKnownAttachments(msgs)
    this.isRunning = false
    this.resumedRunMessageId = null
    this.streamingContent = ''
    this.streamingStep = null
    this.streamingMessageId = null
    this.pendingTimeline = emptyTimeline()
    this.messages = next
    this.hydrated = true
    this.bump()
  }

  private adapterSetMessages(msgs: readonly Message[]): void {
    if (shouldSuppressAdapterSetMessages(this.suppressCancelResync)) {
      this.suppressCancelResync = false
      return
    }
    this.replaceThreadMessages(msgs)
  }

  getAdapter(): ExternalStoreAdapter<Message> {
    return {
      messages: this.messages,
      setMessages: (msgs) => this.adapterSetMessages(msgs),
      isRunning: this.isRunning,
      convertMessage: (msg) => this.convertMessageForRuntime(msg),
      onNew: (message) => this.onNew(message),
      onCancel: () => this.onCancel(),
      suggestions: OMICS_THREAD_SUGGESTIONS,
    }
  }
}
